import { defineStore } from 'pinia'
import {
    CurrentDepartmentResponse,
    CurrentEmployeeJobResponse,
    CurrentEmployeePositionLevelResponse,
    CurrentEmployeePositionResponse,
    Employee,
    TreeRoleViewResponse,
} from '@/api/system/employee'
import { RouteRecordRaw } from 'vue-router'
import { asyncRoutes } from '@/router/asyncModules'
import router, { layoutRoute, staticLayoutRoutes } from '@/router'
import { concatHttp, hasHttpQuery, isHttp, parseHttp, routePathToName, toRoutePath } from '@/common/utils/common'

import { FileResponse } from '@/api/global/file'
import qs from 'qs'

export type UserState = {
    userIsCached: boolean
    user: User
    token: Token
    menuList: Menu[]
}

export type User = {
    // 员工 ID
    id: string
    // 工号
    workNumber: number
    // 手机号码
    // mobilePhone: string
    // 电子邮箱
    // email: string
    // 姓名
    name: string
    // 昵称
    nickname: string
    // 头像文件 URL
    avatarFile: FileResponse
    // 职级
    positionLevel: CurrentEmployeePositionLevelResponse
    // 职务
    position: CurrentEmployeePositionResponse
    // 职位数组
    jobList: CurrentEmployeeJobResponse[]
    // 部门
    department: CurrentDepartmentResponse
    // 角色编码数组
    roleCodeList: string[]
    // 角色权限编码数组
    rolePermissionCodeList: string[]
    // 树形角色视图数组
    treeRoleViewList: TreeRoleViewResponse[]
    waterMark: string[]
}

export type Token = {
    accessToken: string
    refreshToken: string
    expiresIn: number
}

export type Menu = {
    title: string
    label?: string
    icon?: Function
    key: string
    path?: string
    type?: number
    isCategory?: number
    children?: Menu[]
    closable?: boolean
    componentPath?: string
    isOpenNewWindow?: boolean
}

export function viewToRouteRecordRaw(view: TreeRoleViewResponse): RouteRecordRaw {
    const children: RouteRecordRaw[] = []
    if (view.children && view.children.length > 0) {
        view.children.filter(child => child.type === 1).forEach(child => children.push(viewToRouteRecordRaw(child)))
    }

    let componentPath = view.path
    let componentQuery = ''
    let componentQueryQs = {}
    const hasQuery = view.path?.indexOf('?') > -1
    if (hasQuery) {
        const arr = view.path?.split('?')
        componentPath = arr[0]
        componentQuery = arr[1]
        componentQueryQs = qs.parse(componentQuery)
    }
    const component = asyncRoutes[componentPath]
    const routeName = hasQuery
        ? routePathToName(componentPath) + '?' + qs.stringify(componentQueryQs)
        : routePathToName(view.path)
    const routePath = hasQuery
        ? toRoutePath(componentPath) + '?' + qs.stringify(componentQueryQs)
        : toRoutePath(view.path)
    const route: RouteRecordRaw = {
        meta: {
            title: view.name,
            requiresAuth: false,
            icon: 'el-icon-user',
            isKeepAlive: true,
            openKeys: [routeName],
        },
        path: routePath,
        name: routeName,
        children: children,
        component: component,
    }
    if (!route.component) {
        route.redirect = toRoutePath('/system/error/404')
    }
    // console.log(route)
    return route
}

export function routeRecordRawGetOpenKeys(route: RouteRecordRaw, parent?: RouteRecordRaw): RouteRecordRaw {
    const children: RouteRecordRaw[] = []
    if (route.children && route.children.length > 0) {
        const openKeys: string[] = []
        if (parent) {
            if (parent.meta) {
                if (parent.meta.openKeys) {
                    openKeys.push(...(parent.meta.openKeys as string[]))
                }
            }
        }
        openKeys.push(route.name as string)
        route.meta = { ...route.meta, openKeys: openKeys }
        // console.log(route.meta)
        route.children.forEach(child => children.push(routeRecordRawGetOpenKeys(child, route)))
    }
    const openKeys: string[] = []
    if (parent) {
        if (parent.meta) {
            if (parent.meta.openKeys) {
                openKeys.push(...(parent.meta.openKeys as string[]))
            }
        }
    }
    if (route.meta) {
        route.meta.openKeys = openKeys
    }
    return route
}

export function viewToMenu(view: TreeRoleViewResponse): Menu {
    const children: Menu[] = []
    if (view.children && view.children.length > 0) {
        view.children.forEach(child => (child.type === 1 ? children.push(viewToMenu(child)) : null))
    }
    let componentPath = view.path
    let componentQuery = ''
    let componentQueryQs = {}
    const hasQuery = hasHttpQuery(componentPath)
    if (hasQuery) {
        const arr = parseHttp(componentPath)
        if (arr.length == 2) {
            componentPath = arr[0]
            componentQuery = arr[1]
            componentQueryQs = qs.parse(componentQuery)
        }
    }
    const pathIsHttp = isHttp(view.path)
    const routeName = hasQuery
        ? concatHttp(routePathToName(componentPath), qs.stringify(componentQueryQs))
        : routePathToName(view.path)
    const routePath = hasQuery
        ? concatHttp(toRoutePath(componentPath), qs.stringify(componentQueryQs))
        : toRoutePath(view.path)
    const menu: Menu = {
        title: view.name,
        label: view.name,
        key: routeName,
        path: routePath,
        type: view.type,
        isCategory: view.isCategory,
        componentPath: routePath,
        isOpenNewWindow: pathIsHttp,
    }
    if (view.icon) {
        // const icon = Icons[view.icon]
        // if (icon) {
        //     menu.icon = () => h(icon)
        // }
    }
    view.isCategory ? (menu.children = children) : null
    return menu
}

export const useUserStore = defineStore('user', {
    state: (): UserState => {
        return {
            userIsCached: false,
            user: {
                id: '',
                workNumber: 0,
                name: '',
                nickname: '',
                avatarFile: {
                    id: '0',
                    downloadUrl: '',
                },
                // 职级
                positionLevel: {
                    id: '0',
                    name: '',
                    code: '',
                },
                // 职务
                position: {
                    id: '0',
                    name: '',
                    code: '',
                },
                // 职位数组
                jobList: [],
                department: {
                    id: '',
                    name: '',
                    code: '',
                    parentId: '',
                },
                roleCodeList: [],
                rolePermissionCodeList: [],
                treeRoleViewList: [],
                waterMark: [],
            },
            token: {
                accessToken: '',
                refreshToken: '',
                expiresIn: 0,
            },
            menuList: [],
        }
    },
    actions: {
        async getCurrent() {
            const current = await Employee.getCurrent()
            const currentEmployee = current?.data
            this.user = {
                id: currentEmployee?.id,
                workNumber: currentEmployee?.workNumber,
                name: currentEmployee?.name,
                nickname: currentEmployee?.nickname,
                avatarFile: currentEmployee?.avatarFile,
                positionLevel: currentEmployee?.positionLevel,
                position: currentEmployee?.position,
                jobList: currentEmployee?.jobList,
                department: currentEmployee?.department,
                roleCodeList: currentEmployee?.roleCodeList,
                rolePermissionCodeList: currentEmployee?.rolePermissionCodeList,
                treeRoleViewList: currentEmployee?.treeRoleViewList,
                waterMark: [currentEmployee?.name, currentEmployee?.workNumber as unknown as string],
            }
            this.userIsCached = true
            this.setRouteList()
            this.setMenuList()
            return this.user
        },
        setMenuList() {
            const treeRoleViewList = this.user.treeRoleViewList
            const tempMenus: Menu[] = []
            if (treeRoleViewList && treeRoleViewList.length > 0) {
                treeRoleViewList.forEach(view => (view.type === 1 ? tempMenus.push(viewToMenu(view)) : null))
            }
            this.menuList = tempMenus
        },
        setRouteList() {
            // 路由
            const treeRoleViewList = this.user.treeRoleViewList
            let allLayoutChildrenRoutes = [...staticLayoutRoutes]
            if (treeRoleViewList && treeRoleViewList.length > 0) {
                const dynamicLayoutRoutes: RouteRecordRaw[] = []
                treeRoleViewList
                    .filter(item => item.type === 1)
                    .forEach(view => dynamicLayoutRoutes.push(viewToRouteRecordRaw(view)))

                dynamicLayoutRoutes.map(route => routeRecordRawGetOpenKeys(route, undefined))

                allLayoutChildrenRoutes = [...staticLayoutRoutes, ...dynamicLayoutRoutes]
            }
            layoutRoute.children = [...allLayoutChildrenRoutes]
            router.addRoute(layoutRoute)
        },
        reset() {
            this.$reset()
        },
        clear() {
            sessionStorage.removeItem('user')
        },
    },
    persist: {
        storage: sessionStorage,
    },
})
